/* eslint-disable react-refresh/only-export-components */
// context/hook 与 Provider 同文件为有意模式(照 AuthContext);react-refresh 仅关注 HMR,不影响运行时。
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * 军师悬浮球 open 状态的共享 context —— 从 AdvisorLauncher 局部 state 上提,
 * 让工作台等页面的「军师对话」卡片 / 「问军师」快捷也能唤起同一个浮层(不跳页)。
 * Provider 挂在 AdminLayout,与 AdvisorLauncher 同层。
 */
interface AdvisorLauncherState {
  open: boolean;
  openAdvisor: () => void;
  close: () => void;
  setOpen: (v: boolean) => void;
  /** 关闭浮层时把焦点还给当初唤起它的那个控件(见 rememberOpener 注释)。 */
  restoreFocus: () => void;
  /**
   * 首页 hero 小阳是否已"飞走"(下滑越过阈值)。首页顶部 = false(hero 是小阳本体,悬浮球藏);
   * 下滑 = true(hero 飞进右下角,悬浮球现)。非首页恒不看这个(球常驻)。小阳只有一个脸,靠它协调。
   */
  scrolledPastHero: boolean;
  setScrolledPastHero: (v: boolean) => void;
}

const Ctx = createContext<AdvisorLauncherState | null>(null);

/** 唤起者不在了(卡片所在页已卸载)时的兜底:球是全局常驻的,永远还得回去。 */
const LAUNCHER_SELECTOR = "[data-advisor-launcher]";

export function AdvisorLauncherProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // 首页 hero 小阳"飞走"态(scroll-dock);由首页滚动监听驱动,悬浮球据此在首页顶部隐身。
  const [scrolledPastHero, setScrolledPastHero] = useState(false);

  /**
   * ★唤起者(2026-07-15 生产走查):Radix 的 DialogContentModal 在 onCloseAutoFocus 里
   * **主动 preventDefault 掉浏览器的默认焦点归还**,改为 `context.triggerRef.current?.focus()`。
   * 本浮层是受控用法、没有 SheetTrigger(open 上提到本 context 才能让工作台卡片也唤起同一个),
   * 于是 triggerRef 恒为 null → `null?.focus()` 静默 no-op → 关闭后焦点掉到 body,
   * 键盘用户得从页首重新 Tab 一路下来(WCAG 2.4.3)。
   *
   * <p>唤起者有多个(球 / 工作台卡片 / 快捷),不能写死还给球,故在**发起打开那一刻**记住
   * 当时的 activeElement —— 此刻 Radix 还没抢焦点,读到的就是真正的唤起者。
   */
  const openerRef = useRef<HTMLElement | null>(null);

  const rememberOpener = useCallback((v: boolean) => {
    if (v && typeof document !== "undefined") {
      const el = document.activeElement;
      openerRef.current = el instanceof HTMLElement && el !== document.body ? el : null;
    }
    setOpen(v);
  }, []);

  const restoreFocus = useCallback(() => {
    const opener = openerRef.current;
    // isConnected:唤起者可能已随页面卸载(卡片唤起 → 浮层里跳了页)→ 退回常驻的球。
    const target =
      opener?.isConnected === true
        ? opener
        : document.querySelector<HTMLElement>(LAUNCHER_SELECTOR);
    target?.focus();
  }, []);

  const value = useMemo<AdvisorLauncherState>(
    () => ({
      open,
      openAdvisor: () => rememberOpener(true),
      close: () => rememberOpener(false),
      setOpen: rememberOpener,
      restoreFocus,
      scrolledPastHero,
      setScrolledPastHero,
    }),
    [open, rememberOpener, restoreFocus, scrolledPastHero]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAdvisorLauncher(): AdvisorLauncherState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAdvisorLauncher must be used within AdvisorLauncherProvider");
  return v;
}
